import { formatCurrency } from '@/utils/numberFormat';
import { buildInitials } from '@/utils/textValue';
import {
  buildExpenseFeeStatusTabs,
  filterExpenseFeeByStatus,
} from '@/composables/expensesFees/shared/useExpenseFeeStatusTabs';
import {
  buildPaginationPayload,
  resolveTotalPages,
  sliceRowsByPage,
} from '@/composables/shared/usePagination';
import {
  resolveExpenseApproveModalContent,
  resolveExpenseDeleteModalContent,
} from '@/composables/expenses/useExpenseFlow';
import {
  resolveFeeApproveModalContent,
  resolveFeeDeleteModalContent,
} from '@/composables/fees/useFeeFlow';

export const supplierDetailComputed = {
  supplierId() {
    return Number(this.$route?.params?.supplierId || 0);
  },
  supplierName() {
    return String(this.supplier?.name || '').trim() || '-';
  },
  supplierLogo() {
    return String(this.supplier?.logo_thumb_url || this.supplier?.logo_url || this.supplier?.logo || '').trim();
  },
  supplierIsActive() {
    return Number(this.supplier?.active || 0) === 1;
  },
  supplierStatusLabel() {
    return this.supplierIsActive
      ? this.text.detail.status.active
      : this.text.detail.status.inactive;
  },
  supplierInitials() {
    return buildInitials(this.supplierName, { fallback: 'PR' });
  },
  defaultBillingData() {
    const rows = Array.isArray(this.contributors) ? this.contributors.slice() : [];
    if (rows.length === 0) {
      return null;
    }

    rows.sort((left, right) => {
      const leftDefault = Number(left?.default || 0);
      const rightDefault = Number(right?.default || 0);

      if (leftDefault !== rightDefault) {
        return rightDefault - leftDefault;
      }

      return Number(right?.id || 0) - Number(left?.id || 0);
    });

    return rows[0] || null;
  },
  headerActions() {
    return [
      {
        key: 'edit-supplier',
        label: this.text.detail.header.actions.editSupplier,
        icon: 'fa-pencil',
        variant: 'secondary',
      },
      {
        key: 'new-expense',
        label: this.text.detail.header.actions.newExpense,
        icon: 'fa-plus',
        variant: 'primary',
      },
      {
        key: 'new-commission',
        label: this.text.detail.header.actions.newCommission,
        icon: 'fa-percent',
        variant: 'secondary',
      },
    ];
  },
  primaryHeaderActions() {
    const actions = Array.isArray(this.headerActions) ? this.headerActions : [];
    const preferredOrder = ['edit-supplier', 'new-expense'];

    return preferredOrder
      .map((key) => actions.find((action) => String(action?.key || '') === key))
      .filter(Boolean);
  },
  secondaryHeaderActions() {
    const actions = Array.isArray(this.headerActions) ? this.headerActions : [];
    const primaryKeys = new Set(this.primaryHeaderActions.map((action) => String(action?.key || '')));

    return actions.filter((action) => !primaryKeys.has(String(action?.key || '')));
  },
  currentMonthLabel() {
    const formatter = new Intl.DateTimeFormat('es-MX', { month: 'long' });
    const label = formatter.format(new Date());
    return label.charAt(0).toUpperCase() + label.slice(1);
  },
  detailMetrics() {
    const totalProjected = Number(this.supplier?.total_projected || 0);
    const totalPaid = Number(this.supplier?.total_paid || 0);
    const totalProjectedCurrentMonth = Number(this.supplier?.total_projected_current_month || 0);
    const totalPaidCurrentMonth = Number(this.supplier?.total_paid_current_month || 0);
    const projectedCurrentMonthTitle = this.text.detail.metrics.totalProjectedCurrentMonth(this.currentMonthLabel);
    const paidCurrentMonthTitle = this.text.detail.metrics.totalPaidCurrentMonth(this.currentMonthLabel);

    return [
      {
        key: 'total-projected',
        title: this.text.detail.metrics.totalProjected,
        value: formatCurrency(totalProjected),
        valueMeta: '',
        valueScale: 1,
        icon: 'fa-credit-card',
        iconBg: 'rgba(0, 112, 205, 0.12)',
        iconColor: '#0070cd',
      },
      {
        key: 'total-paid',
        title: this.text.detail.metrics.totalPaid,
        value: formatCurrency(totalPaid),
        valueMeta: '',
        valueScale: 1,
        icon: 'fa-check-circle',
        iconBg: 'rgba(59, 212, 46, 0.12)',
        iconColor: '#3bd42e',
      },
      {
        key: 'total-projected-current-month',
        title: projectedCurrentMonthTitle,
        value: formatCurrency(totalProjectedCurrentMonth),
        valueMeta: '',
        valueScale: 1,
        icon: 'fa-line-chart',
        iconBg: 'rgba(0, 178, 226, 0.12)',
        iconColor: '#00b2e2',
      },
      {
        key: 'total-paid-current-month',
        title: paidCurrentMonthTitle,
        value: formatCurrency(totalPaidCurrentMonth),
        valueMeta: '',
        valueScale: 1,
        icon: 'fa-calendar-check-o',
        iconBg: 'rgba(77, 191, 180, 0.12)',
        iconColor: '#4dbfb4',
      },
    ];
  },
  normalizedExpenseRows() {
    return Array.isArray(this.expenseRows) ? this.expenseRows : [];
  },
  expenseStatusTabs() {
    return buildExpenseFeeStatusTabs(this.normalizedExpenseRows);
  },
  filteredExpenseRows() {
    return filterExpenseFeeByStatus(this.normalizedExpenseRows, this.expenseActiveStatusTab);
  },
  expenseTotalPages() {
    return resolveTotalPages(this.filteredExpenseRows.length, this.expensePageSize);
  },
  paginatedExpenseRows() {
    return sliceRowsByPage(this.filteredExpenseRows, this.expenseCurrentPage, this.expensePageSize);
  },
  expensePagination() {
    return buildPaginationPayload({
      currentPage: this.expenseCurrentPage,
      pageSize: this.expensePageSize,
      totalItems: this.filteredExpenseRows.length,
    });
  },
  normalizedFeeRows() {
    return Array.isArray(this.feeRows) ? this.feeRows : [];
  },
  feeStatusTabs() {
    return buildExpenseFeeStatusTabs(this.normalizedFeeRows);
  },
  filteredFeeRows() {
    return filterExpenseFeeByStatus(this.normalizedFeeRows, this.feeActiveStatusTab);
  },
  feeTotalPages() {
    return resolveTotalPages(this.filteredFeeRows.length, this.feePageSize);
  },
  paginatedFeeRows() {
    return sliceRowsByPage(this.filteredFeeRows, this.feeCurrentPage, this.feePageSize);
  },
  feePagination() {
    return buildPaginationPayload({
      currentPage: this.feeCurrentPage,
      pageSize: this.feePageSize,
      totalItems: this.filteredFeeRows.length,
    });
  },
  expenseApproveModal() {
    return resolveExpenseApproveModalContent({
      row: this.approveExpenseTarget,
      approveTargetState: this.approveExpenseTargetState,
      isApproving: this.isApprovingExpense,
    });
  },
  expenseDeleteModal() {
    return resolveExpenseDeleteModalContent({
      row: this.deleteExpenseTarget,
      isDeleting: this.isDeletingExpense,
    });
  },
  feeApproveModal() {
    return resolveFeeApproveModalContent({
      row: this.approveFeeTarget,
      approveTargetState: this.approveFeeTargetState,
      isApproving: this.isApprovingFee,
    });
  },
  feeDeleteModal() {
    return resolveFeeDeleteModalContent({
      row: this.deleteFeeTarget,
      isDeleting: this.isDeletingFee,
    });
  },
  confirmActionTitle() {
    if (this.pendingActionType === 'archive-supplier') {
      return this.text.detail.advancedOptionsConfirm.archive.title;
    }

    if (this.pendingActionType === 'unarchive-supplier') {
      return this.text.detail.advancedOptionsConfirm.unarchive.title;
    }

    return this.text.detail.advancedOptionsConfirm.delete.title;
  },
  confirmActionSubtitle() {
    if (this.pendingActionType === 'archive-supplier') {
      return this.text.detail.advancedOptionsConfirm.archive.subtitle;
    }

    if (this.pendingActionType === 'unarchive-supplier') {
      return this.text.detail.advancedOptionsConfirm.unarchive.subtitle;
    }

    return this.text.detail.advancedOptionsConfirm.delete.subtitle;
  },
  confirmActionMessage() {
    const supplierName = String(this.pendingActionSupplier?.name || this.supplier?.name || '').trim() || this.text.common.noName;

    if (this.pendingActionType === 'archive-supplier') {
      return this.text.detail.advancedOptionsConfirm.archive.message(supplierName);
    }

    if (this.pendingActionType === 'unarchive-supplier') {
      return this.text.detail.advancedOptionsConfirm.unarchive.message(supplierName);
    }

    return this.text.detail.advancedOptionsConfirm.delete.message(supplierName);
  },
  confirmActionButton() {
    if (this.pendingActionType === 'archive-supplier') {
      return this.text.detail.advancedOptionsConfirm.archive.confirm;
    }

    if (this.pendingActionType === 'unarchive-supplier') {
      return this.text.detail.advancedOptionsConfirm.unarchive.confirm;
    }

    return this.text.detail.advancedOptionsConfirm.delete.confirm;
  },
};
